import type { Dispatch } from 'redux';

import { get } from '@wecasa/common/src/api/requester';
import { actionTypes } from '@wecasa/common/src/ducks/actions';
import * as object from '@wecasa/common/src/ducks/object';
import { dispatchError } from '@wecasa/common/src/ducks/utils';
import type { Universe, UniverseReference } from '@wecasa/common/src/types/commons';
import type { Booking, RecurrenceOrder } from '@wecasa/common/src/types/customer';
import type { PayloadAction } from '@wecasa/common/src/types/state';
import moment from '@wecasa/common/src/utils/moment';
import { isCleaningReference } from '@wecasa/common/src/utils/universes';

import { createBasketFromBooking, createCleaningBasketFromBooking } from '../utils/basket';
import type { ModificationBasketState, ModificationMode, OrderChangeRequest, PotentialUpdate } from './types';

const bookingMapper = <T>(booking: T) => ({
  ...booking,
  // TODO TI Booking: Remove this bookingMapper
  // @ts-ignore 'ends_at' only exists for childcare recurring potential and orders
  ends_at: booking.ends_at ? moment(booking.ends_at) : booking.ends_at,
});

// ~~~~~~~~~~ CONSTANTS ~~~~~~~~~~

export const POTENTIAL_UPDATE_START = 'BASKET_POTENTIAL_UPDATE_START';
export const POTENTIAL_UPDATE_SUCCESS = 'BASKET_POTENTIAL_UPDATE_SUCCESS';
export const POTENTIAL_UPDATE_ERROR = 'BASKET_POTENTIAL_UPDATE_ERROR';
export const ORDER_CREATE_CHANGE_REQUEST_START = 'BASKET_ORDER_CREATE_CHANGE_REQUEST_START';
export const ORDER_CREATE_CHANGE_REQUEST_SUCCESS = 'BASKET_ORDER_CREATE_CHANGE_REQUEST_SUCCESS';
export const ORDER_CREATE_CHANGE_REQUEST_ERROR = 'BASKET_ORDER_CREATE_CHANGE_REQUEST_ERROR';
export const MODIFY_PRESTATIONS_INITIALIZATION = 'BASKET_MODIFY_PRESTATIONS_INITIALIZATION';
export const MODIFY_APPOINTMENT_INITIALIZATION = 'BASKET_MODIFY_APPOINTMENT_INITIALIZATION';
export const INIT_CLEANING_MODIFICATION = 'BASKET_INIT_CLEANING_MODIFICATION';
export const MODIFICATION_INITIALIZATION = actionTypes('BASKET_MODIFICATION_INITIALIZATION');
export const CLEAR_MODIFICATION_ERRORS = 'BASKET_CLEAR_MODIFICATION_ERRORS';

// ~~~~~~~~~~ ACTIONS ~~~~~~~~~~

export function clearModificationErrors() {
  return { type: CLEAR_MODIFICATION_ERRORS };
}

export function initializePrestationsModification(booking: Booking, universe: Universe) {
  return { type: MODIFY_PRESTATIONS_INITIALIZATION, booking, universe };
}

export function initializeAppointmentModification(booking: Booking, universe: Universe) {
  return { type: MODIFY_APPOINTMENT_INITIALIZATION, booking, universe };
}

type InitializeCleaningModificationParams = {
  booking: Booking;
  shouldUpdateAllOccurrences?: boolean;
};

// The web modification funnel uses this action instead of initializeModification as
// a different logic for the modification state is needed.
// In the customer app state.basket.bookingToModify.universeAtDate is used for versioned prices/attributes.
// But in future we want to use versioned endpoints (e.g. for services, options etc) instead of
// retrieving everything from universeAtDate (legacy)
export const initializeCleaningModification = ({
  booking,
  shouldUpdateAllOccurrences = true,
}: InitializeCleaningModificationParams) => ({
  type: INIT_CLEANING_MODIFICATION,
  payload: { booking, shouldUpdateAllOccurrences },
});

type InitializeModificationParams = {
  booking: Booking;
  universe: Universe;
  recurrenceOrderPublicId?: RecurrenceOrder['public_id'];
  shouldUpdateAllOccurrences?: boolean;
};

export const initializeModification =
  ({ booking, universe, recurrenceOrderPublicId, shouldUpdateAllOccurrences = true }: InitializeModificationParams) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: MODIFICATION_INITIALIZATION.START });

    if (recurrenceOrderPublicId) {
      try {
        const result = await get(`/customer/orders/${recurrenceOrderPublicId}`);
        dispatch({
          type: MODIFICATION_INITIALIZATION.SUCCESS,
          payload: { booking: result.data, universe, shouldUpdateAllOccurrences },
        });
      } catch (error) {
        dispatchError(dispatch, MODIFICATION_INITIALIZATION.ERROR, error);
      }
    } else {
      dispatch({
        type: MODIFICATION_INITIALIZATION.SUCCESS,
        payload: { booking, universe, shouldUpdateAllOccurrences },
      });
    }
  };

// ~~~~~~~~~~ REDUCER ~~~~~~~~~~

export const initialState: ModificationBasketState = {
  bookingToModify: { ...object.fetchInitialState, universeAtDate: undefined },
  orderChangeRequest: {
    onGoing: false,
    errors: undefined,
    result: null,
  },
  potentialUpdate: {
    onGoing: false,
    errors: undefined,
    result: null,
  },
  updateAllOccurrences: true,
};

/*
 * The handlers are generic over the slice they are composed into: @wecasa/customer merges them with
 * its creation handlers on the same `basket` key, so they receive and return the whole state.
 */
// The action defaults to `any` to match @wecasa/common's `Reducer`, the signature the composed map is checked against.
// biome-ignore lint/suspicious/noExplicitAny: matches the shared Reducer signature
type ModificationHandler<P = any> = <S extends ModificationBasketState>(state: S, action: P) => S;

const onClearModificationErrors: ModificationHandler = (state) => ({
  ...state,
  potentialUpdate: {
    ...state.potentialUpdate,
    errors: undefined,
  },
  orderChangeRequest: {
    ...state.orderChangeRequest,
    errors: undefined,
  },
});

const potentialStartUpdate: ModificationHandler = (state) => ({
  ...state,
  potentialUpdate: {
    ...state.potentialUpdate,
    onGoing: true,
    errors: undefined,
    result: null,
  },
});

type PotentialUpdateErrorPayload = {
  errors: PotentialUpdate['errors'];
};
const potentialUpdateError: ModificationHandler<PayloadAction<PotentialUpdateErrorPayload>> = (state, action) => ({
  ...state,
  potentialUpdate: {
    ...state.potentialUpdate,
    onGoing: false,
    errors: action.payload.errors,
    result: null,
  },
});

type PotentialUpdateSuccessPayload = {
  data: Booking;
};
const potentialUpdated: ModificationHandler<PayloadAction<PotentialUpdateSuccessPayload>> = (state, action) => ({
  ...state,
  potentialUpdate: {
    ...state.potentialUpdate,
    onGoing: false,
    errors: undefined,
    result: bookingMapper<Booking>(action.payload.data),
  },
});

const orderStartChangeRequest: ModificationHandler = (state) => ({
  ...state,
  orderChangeRequest: {
    ...state.orderChangeRequest,
    onGoing: true,
    errors: undefined,
    result: null,
  },
});

type ChangeRequestErrorPayload = {
  errors: OrderChangeRequest['errors'];
};
const orderChangeRequestError: ModificationHandler<PayloadAction<ChangeRequestErrorPayload>> = (state, action) => ({
  ...state,
  orderChangeRequest: {
    ...state.orderChangeRequest,
    onGoing: false,
    errors: action.payload.errors,
    result: null,
  },
});

type ChangeRequestSuccessPayload = {
  data: {
    order: Booking;
  };
};
const orderChangeRequestCreated: ModificationHandler<PayloadAction<ChangeRequestSuccessPayload>> = (state, action) => ({
  ...state,
  orderChangeRequest: {
    ...state.orderChangeRequest,
    onGoing: false,
    errors: undefined,
    result: bookingMapper<Booking>(action.payload.data.order),
  },
});

/*
 * The initialisation handlers below fill the whole basket from the booking being modified, so they
 * also write creation fields (universe, mode, basket content, appointment…). They live here because
 * they belong to the modification flow, not because the fields they touch are modification-only.
 *
 * Those fields are absent from `ModificationBasketState`, and a handler returns the generic `S`, so
 * TS accepts any extra property on the returned literal — `satisfies ModificationWrites` below is
 * what actually checks the universe and mode they write.
 */
type ModificationWrites = {
  universe: UniverseReference;
  mode: ModificationMode;
};

type InitPrestationsModificationPayload = {
  booking: Booking;
  universe: Universe;
};

const onInitPrestationsModification: ModificationHandler<InitPrestationsModificationPayload> = (state, action) => {
  const { universe, booking } = action;
  return {
    ...state,
    ...({ universe: universe.reference, mode: 'modifyPrestations' } satisfies ModificationWrites),
    bookingToModify: {
      ...state.bookingToModify,
      isLoaded: true,
      isLoading: false,
      current: booking,
      universeAtDate: universe,
    },
    ...createBasketFromBooking(booking, universe),
  };
};

type InitAppointmentModificationPayload = {
  booking: Booking;
  universe: Universe;
};
const onInitAppointmentModification: ModificationHandler<InitAppointmentModificationPayload> = (state, action) => {
  const { universe, booking } = action;
  return {
    ...state,
    ...({ universe: universe.reference, mode: 'modifyAppointment' } satisfies ModificationWrites),
    bookingToModify: {
      ...state.bookingToModify,
      isLoaded: true,
      isLoading: false,
      current: booking,
      universeAtDate: universe,
    },
    /**
     * Passing true here means we are resetting the whole appointment object.
     * We need to do this here so that the user can choose a new primary appointment
     * in the modification UI in combination with the updateAppointment utils.
     */
    ...createBasketFromBooking(booking, universe, { clearAppointments: true }),
  };
};

type InitCleaningModificationPayload = {
  booking: Booking;
  shouldUpdateAllOccurrences: boolean;
};

const onInitCleaningModification: ModificationHandler<PayloadAction<InitCleaningModificationPayload>> = (
  state,
  { payload: { booking, shouldUpdateAllOccurrences } },
) => ({
  ...state,
  ...({ universe: booking.universe, mode: 'modification' } satisfies ModificationWrites),
  updateAllOccurrences: shouldUpdateAllOccurrences,
  ...createCleaningBasketFromBooking(booking, { shouldUpdateAllOccurrences }),
  bookingToModify: {
    ...state.bookingToModify,
    isLoaded: true,
    isLoading: false,
    current: booking,
  },
});

const onInitModificationStart: ModificationHandler = (state) => ({
  ...state,
  bookingToModify: object.onFetchStart(state.bookingToModify),
});

type InitModificationSuccessPayload = {
  booking: Booking;
  universe: Universe;
  shouldUpdateAllOccurrences: boolean;
};

const onInitModificationSuccess: ModificationHandler<PayloadAction<InitModificationSuccessPayload>> = (
  state,
  action,
) => {
  const { booking, universe, shouldUpdateAllOccurrences } = action.payload;
  return {
    ...state,
    ...({ universe: universe.reference, mode: 'modification' } satisfies ModificationWrites),
    updateAllOccurrences: shouldUpdateAllOccurrences,
    ...(isCleaningReference(universe.reference)
      ? createCleaningBasketFromBooking(booking, { shouldUpdateAllOccurrences })
      : createBasketFromBooking(booking, universe, { shouldUpdateAllOccurrences })),
    bookingToModify: {
      ...state.bookingToModify,
      isLoaded: true,
      isLoading: false,
      current: booking,
      universeAtDate: universe,
    },
  };
};

type InitModificationErrorPayload = {
  errors: string[];
};
const onInitModificationError: ModificationHandler<PayloadAction<InitModificationErrorPayload>> = (state, action) => ({
  ...state,
  bookingToModify: object.onFetchError(state.bookingToModify, action),
});

export const handlers = {
  [CLEAR_MODIFICATION_ERRORS]: onClearModificationErrors,
  [POTENTIAL_UPDATE_START]: potentialStartUpdate,
  [POTENTIAL_UPDATE_ERROR]: potentialUpdateError,
  [POTENTIAL_UPDATE_SUCCESS]: potentialUpdated,
  [ORDER_CREATE_CHANGE_REQUEST_START]: orderStartChangeRequest,
  [ORDER_CREATE_CHANGE_REQUEST_ERROR]: orderChangeRequestError,
  [ORDER_CREATE_CHANGE_REQUEST_SUCCESS]: orderChangeRequestCreated,
  [MODIFY_PRESTATIONS_INITIALIZATION]: onInitPrestationsModification,
  [MODIFY_APPOINTMENT_INITIALIZATION]: onInitAppointmentModification,
  [INIT_CLEANING_MODIFICATION]: onInitCleaningModification,
  [MODIFICATION_INITIALIZATION.START]: onInitModificationStart,
  [MODIFICATION_INITIALIZATION.SUCCESS]: onInitModificationSuccess,
  [MODIFICATION_INITIALIZATION.ERROR]: onInitModificationError,
};
